/**
 * HTTP 客户端 — 统一响应体解包 + 会话认证 + CSRF 生命周期 + 超时。
 *
 * 契约要点（docs/api/openapi.yaml）：
 * - 所有响应形如 `{ code, data, message }`；`code === 0` 才是成功。
 *   **HTTP 200 不等于业务成功** —— 只看 HTTP 状态是本项目最容易写出的沉默逻辑错误（失效模式 #2）。
 * - 管理端认证走**服务端会话 Cookie** `lh_session`（`HttpOnly; Secure; SameSite=Strict`，
 *   securitySchemes.adminSession 明确「不是 JWT」），因此必须 `credentials: 'include'`，
 *   且前端**拿不到也不应该保存** access token。
 * - 所有非幂等方法另需 `X-CSRF-Token`（GET /admin/auth/csrf 下发），缺失/失配返回 `9003 csrf_invalid`。
 *
 * 关于「401 走 /auth/refresh 刷新」：**契约里没有该端点**，也不可能有 ——
 * 会话是服务端可撤销的不透明 Cookie，续期由服务端在 `admin_sessions` 上滑动完成。
 * 前端能做且已做的两件对应事：
 * 1. `9003` → 重新取 CSRF token 后自动重放一次（这是本契约下唯一真实存在的「刷新并重试」）。
 * 2. `9001` / HTTP 401 → 判定会话失效，通知上层跳登录并带上回跳地址。
 * 详见交付说明中对该指派项的偏离记录。
 */
import type { ApiEnvelope, CsrfData } from './types';

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

/** 请求超时。UI 错误文案里承诺的就是 30s（03-UIUX.md §5.1 Error 态），两处必须一致 */
export const REQUEST_TIMEOUT_MS = 30_000;

/** 管理端错误码（openapi.yaml info「错误码分段」9xxx 段） */
export const ADMIN_CODE = {
  unauthorized: 9001,
  forbidden: 9002,
  csrfInvalid: 9003,
  rateLimited: 9004,
} as const;

/** 传输层失败（无网络 / 超时 / 非 JSON）。业务码不可用 */
export const TRANSPORT_CODE = -1;
/** 端点尚未在 openapi.yaml 中定型 / 后端未实现。UI 应给「未就绪」而非红色错误 */
export const CONTRACT_NOT_READY_CODE = -2;

export class ApiError extends Error {
  readonly code: number;
  readonly httpStatus: number;
  /** 无网络 / 超时 / 解析失败等传输层问题，业务码不可用 */
  readonly transport: boolean;
  /** 仅 9004：服务端建议的重试间隔（秒），来自 Retry-After 响应头 */
  readonly retryAfterSeconds: number | undefined;

  constructor(params: {
    code: number;
    message: string;
    httpStatus: number;
    transport?: boolean;
    retryAfterSeconds?: number;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.httpStatus = params.httpStatus;
    this.transport = params.transport ?? false;
    this.retryAfterSeconds = params.retryAfterSeconds;
  }

  get isUnauthorized(): boolean {
    return this.code === ADMIN_CODE.unauthorized || this.httpStatus === 401;
  }

  get isRateLimited(): boolean {
    return this.code === ADMIN_CODE.rateLimited || this.httpStatus === 429;
  }

  get isContractNotReady(): boolean {
    return this.code === CONTRACT_NOT_READY_CODE;
  }
}

/* ────────────────────────── 会话失效通知 ────────────────────────── */

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** 由 AuthProvider 安装。收到 9001/401 时调用，用于清态 + 跳登录 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/* ────────────────────────── CSRF token 生命周期 ────────────────────────── */

let csrfToken: string | null = null;
/** 并发去重：首屏若同时发出多个变更请求，只取一次 token，避免 N 个 /csrf */
let csrfInFlight: Promise<string> | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** 登出或收到 9003 后作废，下次变更请求会重新拉取 */
export function invalidateCsrfToken(): void {
  csrfToken = null;
  csrfInFlight = null;
}

async function fetchCsrfToken(): Promise<string> {
  // GET /admin/auth/csrf 自身是幂等的、security: []，不能再要求 CSRF 头，否则死循环
  const data = await rawRequest<CsrfData>('/admin/auth/csrf', { method: 'GET' }, null);
  if (typeof data?.csrf_token !== 'string' || data.csrf_token.length === 0) {
    throw new ApiError({
      code: TRANSPORT_CODE,
      message: 'CSRF 端点返回了空令牌，无法发起变更请求。',
      httpStatus: 0,
      transport: true,
    });
  }
  csrfToken = data.csrf_token;
  return data.csrf_token;
}

export async function ensureCsrfToken(): Promise<string> {
  if (csrfToken !== null) return csrfToken;
  csrfInFlight ??= fetchCsrfToken().finally(() => {
    csrfInFlight = null;
  });
  return csrfInFlight;
}

/* ────────────────────────── 查询串 ────────────────────────── */

const UNSAFE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export type QueryValue = string | number | boolean | undefined | null;

/** 把查询对象序列化；`undefined` / `null` / `''` 直接丢弃，避免 `?status=undefined` 这类脏请求 */
export function buildQuery(params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly query?: Record<string, QueryValue>;
  readonly signal?: AbortSignal;
  readonly headers?: Record<string, string>;
  /**
   * 认证类端点（login / me / csrf）自己处理 401，不触发全局跳登录 ——
   * 否则「登录页密码输错」会把用户又踢回登录页，看起来像页面自己刷新了一下。
   */
  readonly skipAuthRedirect?: boolean;
}

function parseRetryAfter(response: Response): number | undefined {
  const raw = response.headers.get('Retry-After');
  if (raw === null) return undefined;
  const seconds = Number.parseInt(raw, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/** 一次纯粹的传输 + 解包，不含 CSRF 获取与重试策略 */
async function rawRequest<T>(
  path: string,
  options: RequestOptions,
  csrf: string | null,
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const url = `${API_BASE}${path}${buildQuery(options.query)}`;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (csrf !== null) headers['X-CSRF-Token'] = csrf;
  if (options.headers !== undefined) {
    for (const [key, value] of Object.entries(options.headers)) {
      headers[key] = value;
    }
  }

  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal =
    options.signal !== undefined ? AbortSignal.any([options.signal, timeout]) : timeout;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      signal,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      // 调用方主动取消（组件卸载 / 竞态丢弃），不是错误，交给上层的 ticket 机制吞掉
      throw cause;
    }
    const timedOut = cause instanceof DOMException && cause.name === 'TimeoutError';
    throw new ApiError({
      code: TRANSPORT_CODE,
      message: timedOut
        ? `请求超时（${String(REQUEST_TIMEOUT_MS / 1000)}s）`
        : '网络请求失败，无法连接服务端',
      httpStatus: 0,
      transport: true,
    });
  }

  // 契约里所有成功响应都带 JSON 信封；204 只作为反向代理层的防御性分支
  if (response.status === 204) return undefined as T;

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    // 404 + 非 JSON = 该路由压根不存在（后端未实现），与「服务端挂了」是两回事
    const notReady = response.status === 404 || response.status === 501;
    throw new ApiError({
      code: notReady ? CONTRACT_NOT_READY_CODE : TRANSPORT_CODE,
      message: notReady
        ? `端点 ${path} 在当前后端上不存在（HTTP ${String(response.status)}）`
        : `服务端返回了非 JSON 响应（HTTP ${String(response.status)}）`,
      httpStatus: response.status,
      transport: true,
    });
  }

  if (typeof envelope?.code !== 'number') {
    throw new ApiError({
      code: TRANSPORT_CODE,
      message: `服务端响应缺少 code 字段，不符合统一响应体契约（HTTP ${String(response.status)}）`,
      httpStatus: response.status,
      transport: true,
    });
  }

  if (envelope.code !== 0) {
    const retryAfter = parseRetryAfter(response);
    throw new ApiError({
      code: envelope.code,
      message: envelope.message || `请求失败（错误码 ${String(envelope.code)}）`,
      httpStatus: response.status,
      ...(retryAfter === undefined ? {} : { retryAfterSeconds: retryAfter }),
    });
  }

  return envelope.data;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const needsCsrf = UNSAFE_METHODS.has(method);

  const attempt = async (csrf: string | null): Promise<T> => rawRequest<T>(path, options, csrf);

  try {
    return await attempt(needsCsrf ? await ensureCsrfToken() : null);
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;

    // 9003：token 过期或服务端轮换过。重取一次并重放 —— 只重放一次，避免无限循环
    if (error.code === ADMIN_CODE.csrfInvalid && needsCsrf) {
      invalidateCsrfToken();
      return attempt(await ensureCsrfToken());
    }

    if (error.isUnauthorized && options.skipAuthRedirect !== true) {
      invalidateCsrfToken();
      onUnauthorized?.();
    }

    throw error;
  }
}

/** 错误 → 面向运营者的可读文案。禁止把裸异常字符串甩到界面上 */
export function describeError(error: unknown, action: string): string {
  if (error instanceof ApiError) {
    if (error.isRateLimited) {
      const wait =
        error.retryAfterSeconds === undefined
          ? '稍后'
          : `${String(error.retryAfterSeconds)} 秒后`;
      return `${action}失败：请求过于频繁，请${wait}重试。`;
    }
    if (error.code === ADMIN_CODE.forbidden) {
      return `${action}失败：当前账号角色没有该操作权限，请联系 owner 调整角色。`;
    }
    if (error.isUnauthorized) {
      return `${action}失败：登录状态已失效，请重新登录。`;
    }
    return `${action}失败：${error.message}`;
  }
  if (error instanceof Error) {
    return `${action}失败：${error.message}`;
  }
  return `${action}失败：未知错误`;
}
